// hooks/use-auth.js
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  checkHasAuthToken,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getAuthUserInfo,
  setAuthUserInfo,
} from '../API/token-util';
import { clearGoogleSession } from '../Utils/googleSession.ts';

/* ------------------------------------------------------------------ */
/*  1. Context (optional) – gives you live React updates if you wrap
/*     part of the tree in <UserAuthProvider>.                        */
/* ------------------------------------------------------------------ */
const UserAuthContext = createContext(null);

export const UserAuthProvider = ({ children }) => {
  const [token, setToken] = useState(getAuthToken());

  /* login / logout -------------------------------------------------- */
  const authorize = useCallback((payload) => {
    setAuthToken(payload);
    setToken(typeof payload === 'string' ? payload : payload.token);
  }, []);

  const unauthorize = useCallback(() => {
    clearGoogleSession();
    removeAuthToken();
    setToken(null);
  }, []);

  /* memoised value so children re-render only on auth change -------- */
  const ctxValue = useMemo(
    () => ({
      token,
      isAuthorized: Boolean(token),
      authorize,
      unauthorize,
      getCurrrentUser: getAuthUserInfo,
      setCurrentUser: setAuthUserInfo,
    }),
    [token, authorize, unauthorize]
  );

  return (
    <UserAuthContext.Provider value={ctxValue}>
      {children}
    </UserAuthContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/*  2. Hook – works WITH OR WITHOUT the provider                      */
/* ------------------------------------------------------------------ */
export default function useAuth() {
  const ctx = useContext(UserAuthContext);

  /* If a provider is present, use its reactive value. --------------- */
  if (ctx) return ctx;

  /* Fallback: stateless helpers (no React re-renders). -------------- */
  return {
    token:        getAuthToken(),
    isAuthorized: checkHasAuthToken(),
    authorize(payload) {
      setAuthToken(payload);
      /* no setState here – components won’t re-render without provider */
    },
    unauthorize() {
      clearGoogleSession();
      removeAuthToken();
    },
    getCurrrentUser: getAuthUserInfo,
    setCurrentUser: setAuthUserInfo,
  };
}
